import { useEffect, useRef, useState } from 'react'
import type { ScheduleEntry } from '@myapp/contract'
import { useAddTaskTechnicianAttachments, useCompleteTask } from '../../../entities/task'
import Modal from '../../../shared/ui/overlay/Modal/Modal'
import { getStartWorkErrorMessage } from '../model/getStartWorkErrorMessage'
import {
  changedFields,
  nowInstant,
  sessionTimesFrom,
  type SessionTimes,
} from '../model/workSessionTime'
import { SessionTimesDisclosure } from './SessionTimesDisclosure'
import { WorkTimeSummary } from './WorkTimeSummary'
import { CommentDisclosure, commentPatch } from './CommentDisclosure'
import { FilesDisclosure } from './FilesDisclosure'

const EDITABLE: Array<keyof SessionTimes> = [
  'travelStartedAt',
  'actualWorkStartsAt',
  'actualEndsAt',
]

interface Props {
  show: boolean
  setShow: (show: boolean) => void
  taskId: number
  schedule: ScheduleEntry
  /** What the worker already wrote during the job — prefilled, so the sheet edits rather than replaces. */
  technicianComment?: string | null
  onSuccess: () => void
}

/**
 * "Arbeit abschließen" for a task — the same sign-off as the appointment, comment and files
 * included. Files go up first on their own route, then the sign-off; see the appointment sheet
 * for why the two are separate requests.
 */
export function CompleteTaskModal({
  show,
  setShow,
  taskId,
  schedule,
  technicianComment,
  onSuccess,
}: Props) {
  const completeTask = useCompleteTask()
  const addAttachments = useAddTaskTechnicianAttachments()
  const [times, setTimes] = useState<SessionTimes | null>(null)
  const [initial, setInitial] = useState<SessionTimes | null>(null)
  const [showTimes, setShowTimes] = useState(false)
  const [showComment, setShowComment] = useState(false)
  const [showFiles, setShowFiles] = useState(false)
  const [comment, setComment] = useState('')
  const [files, setFiles] = useState<File[]>([])
  const [submitError, setSubmitError] = useState<string | null>(null)
  const uploadedRef = useRef(false)

  useEffect(() => {
    if (!show) return
    completeTask.reset()
    addAttachments.reset()
    const start = sessionTimesFrom(schedule, { field: 'actualEndsAt', value: nowInstant() })
    setInitial(start)
    setTimes(start)
    setShowTimes(false)
    setComment(technicianComment ?? '')
    setShowComment(Boolean(technicianComment))
    setShowFiles(false)
    setFiles([])
    setSubmitError(null)
    uploadedRef.current = false
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, schedule.actualWorkStartsAt])

  const isPending = completeTask.isPending || addAttachments.isPending

  const onSubmit = async () => {
    if (!times || !initial || isPending) return
    setSubmitError(null)
    try {
      if (files.length && !uploadedRef.current) {
        await addAttachments.mutateAsync({ id: taskId, files })
        uploadedRef.current = true
        setFiles([])
      }
      await completeTask.mutateAsync({
        id: taskId,
        dto: {
          ...changedFields(initial, times, EDITABLE),
          // Only when it changed — see the appointment sheet.
          ...commentPatch(technicianComment, comment),
        },
      })
      onSuccess()
    } catch (error) {
      setSubmitError(getStartWorkErrorMessage(error))
    }
  }

  return (
    <Modal
      show={show}
      setShow={setShow}
      title="Arbeit abschließen"
      submitButton={{ title: 'Arbeit abschließen', isPending, onSubmit }}
    >
      <div className="flex w-full flex-col gap-4">
        <WorkTimeSummary actualStart={schedule.actualWorkStartsAt} />

        {times && (
          <SessionTimesDisclosure
            times={times}
            editable={EDITABLE}
            onChange={setTimes}
            open={showTimes}
            onToggle={() => setShowTimes((v) => !v)}
          />
        )}

        <CommentDisclosure
          value={comment}
          onChange={setComment}
          open={showComment}
          onToggle={() => setShowComment((v) => !v)}
        />

        <FilesDisclosure
          files={files}
          onChange={setFiles}
          open={showFiles}
          onToggle={() => setShowFiles((v) => !v)}
        />

        {submitError && (
          <p role="alert" className="text-sm font-medium text-red-500">
            {submitError}
          </p>
        )}
      </div>
    </Modal>
  )
}
